import { paiementEtat } from './paiements-etat';
import { litDate } from './periodes';

/**
 * Mois entre deux paiements d'une dépense qui revient.
 *
 * <p>Chaque entreprise paie la TVA, l'AVS et ses assurances à son rythme : à
 * l'année, au semestre (janvier et juillet), au trimestre ou au mois.
 */
export const MOIS_ENTRE_PAIEMENTS = { mensuel: 1, trimestriel: 3, semestriel: 6, annuel: 12 };

/**
 * Un identifiant de série, unique : celui du navigateur quand il en donne un,
 * sinon tiré au hasard sur le même modèle.
 *
 * @returns {string} 36 caractères, lettres, chiffres et tirets
 */
export function nouvelleSerie() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : (r % 4) + 8).toString(16);
  });
}

/**
 * Les mois d'un paiement qui revient, du mois de départ au mois en cours.
 *
 * @param {string} depuis premier mois, au format `AAAA-MM`
 * @param {Date} [maintenant] date de référence
 * @param {number} [pas] mois entre deux paiements : 1 chaque mois, 3 chaque
 *   trimestre, 6 chaque semestre, 12 chaque année
 * @returns {{annee: number, mois: number}[]} les mois, le plus ancien en tête
 */
export function moisDepuis(depuis, maintenant = new Date(), pas = 1) {
  const [a, m] = String(depuis || '')
    .split('-')
    .map(Number);
  if (!a || !m) return [];
  const fin = maintenant.getFullYear() * 12 + maintenant.getMonth();
  const saut = Math.max(1, Math.round(+pas || 1));
  const sortie = [];
  for (let n = a * 12 + (m - 1); n <= fin; n += saut) {
    sortie.push({ annee: Math.floor(n / 12), mois: (n % 12) + 1 });
  }
  return sortie;
}

/**
 * Une dépense payée chaque mois — le loyer du dépôt — inscrite pour chaque mois
 * depuis le mois choisi jusqu'au mois en cours.
 *
 * <p>Le patron prenait en photo la facture du dépôt, qu'il paie tous les mois,
 * et devait la saisir dix fois pour avoir l'année. Chaque mois devient une
 * dépense, au même jour du mois que la facture (le dernier jour d'un mois plus
 * court) ; le justificatif reste sur le mois de la facture, sans être copié
 * dix fois, et l'IBAN aussi : les mois passés sont payés, ils n'ont pas à
 * proposer « Payer ».
 *
 * @param {object} depense la dépense saisie (date `JJ.MM.AAAA`)
 * @param {string} depuis premier mois, au format `AAAA-MM`
 * @param {Date} [maintenant] date de référence
 * @returns {object[]} une dépense par mois, la plus récente en tête
 */
export function depensesMensuelles(depense, depuis, maintenant = new Date()) {
  return depensesPeriodiques(depense, depuis, 'mensuel', maintenant);
}

/**
 * Une dépense payée chaque mois, trimestre, semestre ou année — la TVA, l'AVS,
 * une assurance — inscrite pour chaque paiement depuis le premier jusqu'au
 * mois en cours, comme le loyer payé chaque mois : une dépense par paiement,
 * au même jour du mois que la facture, le justificatif et l'IBAN sur le seul
 * paiement de la facture.
 *
 * <p>Les paiements partagent l'identifiant de leur série : chaque nuit, le
 * serveur note tout seul les suivants, au même jour du mois, sans
 * justificatif ni IBAN, jusqu'à ce que le patron arrête la série. Il ne note
 * jamais deux fois le même paiement.
 *
 * <p>« Répartir » : un paiement qui couvre plusieurs mois (1'200.– en janvier
 * pour l'année) garde sa date, sa pièce et sa TVA ; il porte seulement le
 * nombre de mois qu'il couvre, pour que sa charge se compte un douzième par
 * mois. Pas pour un paiement à l'État : ce n'est pas une charge.
 *
 * @param {object} depense la dépense saisie (date `JJ.MM.AAAA`)
 * @param {string} depuis premier paiement, au format `AAAA-MM`
 * @param {string} frequence `mensuel`, `trimestriel`, `semestriel` ou `annuel`
 * @param {Date} [maintenant] date de référence
 * @param {{repartir?: boolean}} [options] répartir chaque paiement sur ses mois
 * @returns {object[]} une dépense par paiement, la plus récente en tête
 */
export function depensesPeriodiques(
  depense,
  depuis,
  frequence,
  maintenant = new Date(),
  { repartir = false } = {},
) {
  const pas = MOIS_ENTRE_PAIEMENTS[frequence] || 1;
  const lue = litDate(depense.date, maintenant) || maintenant;
  const jour = lue.getDate();
  const moisPiece = lue.getFullYear() * 12 + lue.getMonth();
  const mois = moisDepuis(depuis, maintenant, pas);
  const avecPiece = mois.some((x) => x.annee * 12 + x.mois - 1 === moisPiece)
    ? moisPiece
    : mois.length
      ? mois[mois.length - 1].annee * 12 + mois[mois.length - 1].mois - 1
      : null;
  const { mensuel: _m, depuis: _d, repartir: _r, ...base } = depense;
  const couverts = repartir && pas > 1 && !paiementEtat(base.type) ? pas : null;
  // Un identifiant partagé par les paiements : le serveur poursuit la série.
  const serie = nouvelleSerie();
  return mois
    .map(({ annee, mois: m }) => {
      const dernier = new Date(annee, m, 0).getDate();
      const mm = String(m).padStart(2, '0');
      const date = String(Math.min(jour, dernier)).padStart(2, '0') + '.' + mm + '.' + annee;
      const garde = annee * 12 + m - 1 === avecPiece;
      return {
        ...base,
        date,
        libelle: (base.libelle || '') + ' — ' + mm + '.' + annee,
        frequence: MOIS_ENTRE_PAIEMENTS[frequence] ? frequence : 'mensuel',
        moisCouverts: couverts,
        serie,
        ...(garde ? {} : { piece: '', pieceNom: '', pieceMime: '', iban: '' }),
      };
    })
    .reverse();
}

/**
 * Place, parmi les factures fournisseur d'un chantier, de celle qu'une dépense
 * y a inscrite en s'enregistrant.
 *
 * Seuls le montant et la date étaient comparés : effacer une dépense de 50.–
 * emportait la première facture de 50.– du même jour, fût-elle d'un autre
 * fournisseur, saisie sur le chantier. Il faut désormais le même montant, la
 * même date et le même fournisseur — celui que la dépense y a écrit : son
 * libellé, à défaut sa catégorie. Le lien exact, quand il existe, prime.
 *
 * @param {object[]} factures factures fournisseur du chantier
 * @param {object} depense dépense effacée
 * @param {string} [categorie] libellé de sa catégorie
 * @returns {number} la place de la facture, ou -1
 */
export function factureDeLaDepense(factures, depense, categorie) {
  const liste = Array.isArray(factures) ? factures : [];
  const parLien = liste.findIndex((fa) => fa?.depenseId != null && fa.depenseId === depense?.id);
  if (parLien >= 0) return parLien;
  const nom = (v) =>
    String(v ?? '')
      .trim()
      .toLowerCase();
  // Le nom que la dépense a écrit sur le chantier : celui du fournisseur
  // (avant « — adresse »), à défaut son libellé, à défaut sa catégorie. Seul
  // le libellé était comparé : une dépense « Essence camion » chez « Shell »
  // ne retrouvait jamais sa facture, qui restait comptée après suppression.
  const noms = new Set(
    [
      String(depense?.fournisseur || '').split(' — ')[0],
      depense?.libelle,
      depense?.libelle ? '' : categorie,
    ]
      .map(nom)
      .filter(Boolean),
  );
  if (noms.size === 0) return -1;
  return liste.findIndex(
    (fa) =>
      (fa?.depenseId == null || fa.depenseId === depense?.id) &&
      (+fa.montant || 0).toFixed(2) === (+depense.montant || 0).toFixed(2) &&
      fa.date === depense.date &&
      noms.has(nom(fa.fournisseur)),
  );
}

/** « Dépôt — 10.2026 » → « Dépôt » : le libellé sans le mois de la série. */
const sansLeMois = (libelle) => String(libelle || '').replace(/\s+—\s+\d{2}\.\d{4}$/, '');

/** « Dépôt — 10.2026 » → « — 10.2026 » : le mois que la série a ajouté. */
const leMois = (libelle) => (String(libelle || '').match(/\s+—\s+\d{2}\.\d{4}$/) || [''])[0];

/**
 * Les autres mois d'une dépense payée chaque mois : même libellé (sans le
 * mois), même montant, même catégorie.
 *
 * <p>Le loyer du dépôt, saisi une fois pour dix mois, rangé dans la mauvaise
 * catégorie, se corrigeait mois par mois : dix fois la même retouche.
 *
 * @param {object[]} frais dépenses
 * @param {object} depense la dépense modifiée, telle qu'enregistrée
 * @returns {object[]} les autres mois de la série, vide si elle est seule
 */
export function autresMois(frais, depense) {
  if (!depense) return [];
  // La série enregistrée fait foi : deux leasings du même montant et du même
  // nom, sur deux voitures, ne se corrigent pas l'un l'autre.
  if (depense.serie) {
    return (frais || []).filter((x) => x.id !== depense.id && x.serie === depense.serie);
  }
  if (!leMois(depense.libelle)) return [];
  const base = sansLeMois(depense.libelle);
  return (frais || []).filter(
    (x) =>
      x.id !== depense.id &&
      leMois(x.libelle) &&
      sansLeMois(x.libelle) === base &&
      Number(x.montant) === Number(depense.montant) &&
      x.type === depense.type,
  );
}

/**
 * Applique à un autre mois de la série ce qui a été corrigé : catégorie,
 * fournisseur, montant, TVA, libellé. Sa date, son mois et son justificatif
 * restent les siens.
 *
 * @param {object} mois un autre mois de la série
 * @param {object} corrigee la dépense corrigée
 * @returns {object} le mois corrigé
 */
export function corrigeLeMois(mois, corrigee) {
  return {
    ...mois,
    type: corrigee.type,
    fournisseur: corrigee.fournisseur,
    montant: corrigee.montant,
    tva: corrigee.tva,
    montantTva: corrigee.montantTva,
    paiement: corrigee.paiement,
    // Répartie ou non sur ses mois : toute la série, comme la catégorie.
    moisCouverts: corrigee.moisCouverts ?? null,
    libelle: sansLeMois(corrigee.libelle) + leMois(mois.libelle),
  };
}
